/* finger-chooser/palette.js — colours and shapes of the rings.
 *
 * Every finger is told apart by TWO things, never by hue alone: its colour
 * and its shape (circle, triangle, square, …). About one man in twelve does
 * not see red and green apart; with a shape on every ring the chosen finger
 * is still unambiguous. The pairing colour↔shape is fixed per slot, so the
 * spoken name of a finger («blue plus») is one translated phrase per slot.
 *
 * Shapes are SVG path data in a 24×24 box centred on (12, 12): the same
 * string draws on the canvas (Path2D) and in the names list (<svg>).
 */

function poly(n, r, rot, inner) {
    const pts = [];
    const k = inner ? n * 2 : n;
    for (let i = 0; i < k; i++) {
        const rr = inner && (i % 2) ? inner : r;
        const a = rot + (i * Math.PI * 2) / k;
        pts.push((12 + rr * Math.sin(a)).toFixed(2) + ' ' + (12 - rr * Math.cos(a)).toFixed(2));
    }
    return 'M' + pts.join('L') + 'Z';
}

export const SHAPES = {
    circle: 'M3.5 12a8.5 8.5 0 1 0 17 0a8.5 8.5 0 1 0 -17 0Z',
    triangle: 'M12 3L21.5 19.5H2.5Z',
    square: 'M4.5 4.5H19.5V19.5H4.5Z',
    diamond: 'M12 2L22 12L12 22L2 12Z',
    star: poly(5, 10, 0, 4.2),
    plus: 'M9 3H15V9H21V15H15V21H9V15H3V9H9Z',
    hexagon: poly(6, 9.5, Math.PI / 6),
    heart: 'M12 20.8C5.2 15.6 2.5 12 2.5 8.4C2.5 5.5 4.7 3.4 7.4 3.4C9.4 3.4 11 4.6 12 6.4C13 4.6 14.6 3.4 16.6 3.4C19.3 3.4 21.5 5.5 21.5 8.4C21.5 12 18.8 15.6 12 20.8Z',
    pentagon: poly(5, 9.8, 0),
    cross: 'M5.3 3.2L12 9.9L18.7 3.2L20.8 5.3L14.1 12L20.8 18.7L18.7 20.8L12 14.1L5.3 20.8L3.2 18.7L9.9 12L3.2 5.3Z',
};

/* Finger slots. `ink` is the colour that reads ON the swatch (for the shape
 * or a number inside a badge). Tuned for the dark play field. */
export const FINGER = [
    { hex: '#ff4d5e', ink: '#ffffff', shape: 'circle' },
    { hex: '#ff9f1c', ink: '#1a1200', shape: 'triangle' },
    { hex: '#ffe14d', ink: '#1a1600', shape: 'square' },
    { hex: '#3ddc84', ink: '#002612', shape: 'diamond' },
    { hex: '#2fd0f5', ink: '#00202a', shape: 'star' },
    { hex: '#4d7cff', ink: '#ffffff', shape: 'plus' },
    { hex: '#b26bff', ink: '#ffffff', shape: 'hexagon' },
    { hex: '#ff5fb4', ink: '#2a0016', shape: 'heart' },
    { hex: '#eef2f7', ink: '#111827', shape: 'pentagon' },
    { hex: '#c88a4a', ink: '#1a0e00', shape: 'cross' },
];

export const TEAM = [
    { hex: '#3b82f6', ink: '#ffffff', shape: 'circle' },
    { hex: '#f97316', ink: '#1a0a00', shape: 'triangle' },
    { hex: '#22c55e', ink: '#00210d', shape: 'square' },
    { hex: '#a855f7', ink: '#ffffff', shape: 'diamond' },
    { hex: '#facc15', ink: '#1a1500', shape: 'star' },
    { hex: '#ec4899', ink: '#ffffff', shape: 'heart' },
];

export function finger(slot) {
    const s = Math.max(0, Math.floor(slot || 0));
    return FINGER[s % FINGER.length];
}

export function team(t) {
    return TEAM[Math.max(0, Math.floor(t || 0)) % TEAM.length];
}

export function rgba(hex, a) {
    const h = hex.replace('#', '');
    const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
    return 'rgba(' + ((n >> 16) & 255) + ',' + ((n >> 8) & 255) + ',' + (n & 255) + ',' + a + ')';
}

/* Relative luminance (WCAG) of a #rrggbb colour. */
export function luminance(hex) {
    const h = hex.replace('#', '');
    const n = parseInt(h, 16);
    const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
}

/* Black or white — whichever reads better on this background. */
export function inkOn(hex) {
    return luminance(hex) > 0.36 ? '#111111' : '#ffffff';
}
